import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../components/Header';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { can } from '../../utils/permissions';
import GuideFeeTiersEditor, { getGuideFeeTierDuplicateError } from './GuideFeeTiersEditor';

const saveBtnSx = (colors) => ({
  backgroundColor: colors.blueAccent[700],
  color: colors.grey[100],
  width: 'fit-content',
  fontSize: '14px',
  fontWeight: 'bold',
  padding: '10px 24px',
  textTransform: 'none',
  '&:hover': { backgroundColor: colors.blueAccent[800] },
});

const CountryExcursionDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);

  const [details, setDetails] = useState({
    CountryID: '',
    CountryName: '',
    departureDate: '',
    excursions: [],
    packages: [],
    pdf: '',
    pdf_raw: '',
  });

  const [excursions, setExcursions] = useState([]);
  /** excursionId -> max quantity string, empty = unlimited */
  const [maxQtyByExcursion, setMaxQtyByExcursion] = useState({});
  /** excursionId -> price override string for this departure only, empty = use the excursion's global price */
  const [priceByExcursion, setPriceByExcursion] = useState({});
  const [guideFeeTiers, setGuideFeeTiers] = useState([]);
  const guideFeeTierValidationError = getGuideFeeTierDuplicateError(guideFeeTiers);

  /** All packages for this country, with their global price and any existing departure override */
  const [packages, setPackages] = useState([]);
  /** packageId -> price override string for this departure only, empty = use the package's global price */
  const [priceByPackage, setPriceByPackage] = useState({});

  const getDetails = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion/${id}`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        const d = data?.data;
        const pkgList = d.packageSettings || [];
        const selectedPackageIds = pkgList.filter((p) => p.selected).map((p) => p.packageId);
        setDetails({ ...d, packages: selectedPackageIds });
        const mq = {};
        const pr = {};
        (d.excursionSettings || []).forEach((s) => {
          mq[s.excursionId] =
            s.maxQuantity != null && s.maxQuantity !== '' ? String(s.maxQuantity) : '';
          pr[s.excursionId] = s.price != null && s.price !== '' ? String(s.price) : '';
        });
        setMaxQtyByExcursion(mq);
        setPriceByExcursion(pr);

        setPackages(pkgList);
        const pkgPrices = {};
        pkgList.forEach((p) => {
          pkgPrices[p.packageId] = p.price != null && p.price !== '' ? String(p.price) : '';
        });
        setPriceByPackage(pkgPrices);

        const tg = d.tripGuideFees?.guideFeeTiers || [];
        setGuideFeeTiers(
          tg.map((t, i) => ({
            key: `tier-${i}-${t.sortOrder ?? i}`,
            minEur: t.minEur != null ? String(t.minEur) : '',
            maxEur: t.maxEur != null && t.maxEur !== '' ? String(t.maxEur) : '',
            percentage: t.percentage != null ? String(t.percentage) : '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching destination excursion details:', error);
    }
  };

  const getExcursions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countryExcursions/${details.CountryID}`, {
        headers: { token },
      });
      const data = await res.json();
      setExcursions(data.excursions);
    } catch (error) {
      console.error('Error fetching excursions:', error);
    }
  };

  useEffect(() => {
    getDetails();
  }, []);

  useEffect(() => {
    if (details.CountryID) {
      getExcursions();
    }
  }, [details.CountryID]);

  const handleChange = (e) => {
    setDetails({ ...details, [e.target.name]: e.target.value });
  };

  const handleChecked = (e, excursionId) => {
    const newExcursions = e.target.checked
      ? [...details.excursions, excursionId]
      : details.excursions.filter((id) => id !== excursionId);
    setDetails({ ...details, excursions: newExcursions });
    if (e.target.checked) {
      setMaxQtyByExcursion((prev) => ({
        ...prev,
        [excursionId]: prev[excursionId] ?? '',
      }));
      setPriceByExcursion((prev) => ({
        ...prev,
        [excursionId]: prev[excursionId] ?? '',
      }));
    } else {
      setMaxQtyByExcursion((prev) => {
        const next = { ...prev };
        delete next[excursionId];
        return next;
      });
      setPriceByExcursion((prev) => {
        const next = { ...prev };
        delete next[excursionId];
        return next;
      });
    }
  };

  const setMaxQtyFor = (excursionId, value) => {
    setMaxQtyByExcursion((prev) => ({ ...prev, [excursionId]: value }));
  };

  const setPriceFor = (excursionId, value) => {
    setPriceByExcursion((prev) => ({ ...prev, [excursionId]: value }));
  };

  const setPriceForPackage = (packageId, value) => {
    setPriceByPackage((prev) => ({ ...prev, [packageId]: value }));
  };

  const handlePackageChecked = (e, packageId) => {
    const checked = e.target.checked;
    const newPackages = checked
      ? [...details.packages, packageId]
      : details.packages.filter((id) => id !== packageId);

    if (!checked) {
      setDetails({ ...details, packages: newPackages });
      return;
    }

    // Selecting a package auto-selects the excursions included in it.
    const pkgExcursionIds = packages.find((p) => p.packageId === packageId)?.excursionIds || [];
    const newExcursions = Array.from(new Set([...details.excursions, ...pkgExcursionIds]));
    setDetails({ ...details, packages: newPackages, excursions: newExcursions });

    setMaxQtyByExcursion((prev) => {
      const next = { ...prev };
      pkgExcursionIds.forEach((exId) => {
        if (next[exId] === undefined) next[exId] = '';
      });
      return next;
    });
    setPriceByExcursion((prev) => {
      const next = { ...prev };
      pkgExcursionIds.forEach((exId) => {
        if (next[exId] === undefined) next[exId] = '';
      });
      return next;
    });
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPdfLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion/uploadpdf`, {
        method: 'POST',
        headers: { token },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setDetails({ ...details, pdf: `${process.env.REACT_APP_APILINK}/uploads/pdf/${data.pdf}`, pdf_raw: data.pdf });
        setOpenToast(true);
        setToastmessage('PDF uploaded successfully');
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Error uploading PDF');
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setOpenToast(true);
      setToastmessage('Error uploading PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClearPdf = () => {
    setDetails({ ...details, pdf: null, pdf_raw: null });
  };

  const handlevalidation = () => {
    const { CountryID, departureDate, excursions } = details;
    if (!excursions || excursions.length === 0) {
      setOpenToast(true);
      setToastmessage('Atleast one excursion should be selected');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (handlevalidation()) {
      const dupMsg = getGuideFeeTierDuplicateError(guideFeeTiers);
      if (dupMsg) {
        setOpenToast(true);
        setToastmessage(dupMsg);
        setLoading(false);
        return;
      }
      for (const excursionId of details.excursions) {
        const raw = maxQtyByExcursion[excursionId];
        if (raw !== '' && raw != null && String(raw).trim() !== '') {
          const n = parseInt(String(raw), 10);
          if (Number.isNaN(n) || n < 0) {
            setOpenToast(true);
            setToastmessage('Max cap per excursion must be a non-negative whole number (or leave blank for no limit).');
            setLoading(false);
            return;
          }
        }
        const rawPrice = priceByExcursion[excursionId];
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const p = Number(rawPrice);
          if (!Number.isFinite(p) || p < 0) {
            setOpenToast(true);
            setToastmessage('Price override per excursion must be a non-negative number (or leave blank to use the global price).');
            setLoading(false);
            return;
          }
        }
      }
      for (const pkg of packages) {
        const rawPrice = priceByPackage[pkg.packageId];
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const p = Number(rawPrice);
          if (!Number.isFinite(p) || p < 0) {
            setOpenToast(true);
            setToastmessage('Price override per package must be a non-negative number (or leave blank to use the global price).');
            setLoading(false);
            return;
          }
        }
      }
      const excursionsPayload = details.excursions.map((excursionId) => {
        const raw = maxQtyByExcursion[excursionId];
        let maxQuantity = null;
        if (raw !== '' && raw != null && String(raw).trim() !== '') {
          const n = parseInt(String(raw), 10);
          maxQuantity = Number.isNaN(n) ? null : n;
        }
        const rawPrice = priceByExcursion[excursionId];
        let price = null;
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const globalPrice = excursions.find((e) => e.ExcursionID === excursionId)?.ExcursionPrice;
          const p = Number(rawPrice);
          price = Number.isFinite(p) && p !== Number(globalPrice) ? p : null;
        }
        return { excursionId, maxQuantity, price };
      });
      const tierBody = guideFeeTiers.map(({ minEur, maxEur, percentage }) => ({ minEur, maxEur, percentage }));
      const packagesPayload = packages
        .filter((pkg) => details.packages.includes(pkg.packageId))
        .map((pkg) => {
          const rawPrice = priceByPackage[pkg.packageId];
          let price = null;
          if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
            const p = Number(rawPrice);
            price = Number.isFinite(p) && p !== Number(pkg.packagePrice) ? p : null;
          }
          return { packageId: pkg.packageId, price };
        });

      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion/${id}`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          token,
        },
        body: JSON.stringify({
          ...details,
          pdf: details.pdf_raw,
          excursions: excursionsPayload,
          tripGuideFees: { guideFeeTiers: tierBody },
          packages: packagesPayload,
        }),
      });
      const data = await res.json();
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/country-excursion');
          }, 2000);
        }
      }
    }
    setLoading(false);
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="DESTINATION EXCURSION DETAILS" subtitle="Destination Excursion Details" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form ref={formRef} onSubmit={handleFormSubmit}>
        <Box
          style={{
            display: 'grid',
            gap: '1.25rem',
            paddingBottom: '20px',
          }}
        >
          {/* Country */}
          <TextField
            type="text"
            fullWidth
            variant="filled"
            label="Destination"
            onChange={handleChange}
            value={details.CountryName}
            name="CountryName"
            InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
            style={{ colorScheme: 'dark' }}
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#fff',
              },
            }}
          />

          {/* Departure Date */}
          <TextField
            type="text"
            fullWidth
            variant="filled"
            label="Departure Date"
            value={details.departureDate}
            InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
            style={{ colorScheme: 'dark' }}
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#fff',
              },
            }}
          />

          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
            }}
          >
            <Typography variant="h5" fontSize="18px" fontWeight="bold" sx={{ color: colors.grey[100] }}>
              Guide fee ranges (% of paid collections)
            </Typography>
            <GuideFeeTiersEditor value={guideFeeTiers} onChange={setGuideFeeTiers} colors={colors} />
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <Button
                type="button"
                variant="contained"
                disabled={loading || Boolean(guideFeeTierValidationError)}
                title={guideFeeTierValidationError || undefined}
                onClick={() => formRef.current?.requestSubmit()}
                sx={saveBtnSx(colors)}
              >
                Save
              </Button>
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                Saves destination, guide fee ranges, excursions, and PDF together.
              </Typography>
            </Box>
          </Box>

          {/* Packages */}
          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
            }}
          >
            <Typography variant="h5" fontSize="18px" fontWeight="bold">
              Packages
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
              Only checked packages will be visible/bookable to passengers on this departure date.
              Selecting a package auto-selects the excursions included in it. Set a price override
              only if this departure should charge a different price than the package's global price.
            </Typography>
            <div style={{ display: 'grid', gap: '8px' }}>
              {packages?.length > 0 ? (
                packages.map((pkg) => (
                  <Box key={pkg.packageId} display="flex" flexWrap="wrap" alignItems="center" gap={1} width="100%">
                    <FormControlLabel
                      sx={{ display: 'flex', m: 0, flex: 1, minWidth: 200 }}
                      control={
                        <Checkbox
                          checked={details.packages.includes(pkg.packageId)}
                          onChange={(e) => handlePackageChecked(e, pkg.packageId)}
                          color="success"
                        />
                      }
                      label={pkg.packageName}
                    />
                    {details.packages.includes(pkg.packageId) && (
                      <Box display="flex" flexDirection="column" gap="2px">
                        {pkg.packagePrice != null && (
                          <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                            Global Price: {pkg.packagePrice}
                          </Typography>
                        )}
                        <TextField
                          size="small"
                          variant="filled"
                          type="number"
                          label="Price override (blank = global)"
                          disabled={!can('prices.edit')}
                          value={priceByPackage[pkg.packageId] ?? ''}
                          onChange={(e) => setPriceForPackage(pkg.packageId, e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 220 }}
                        />
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <i>No packages found for this destination.</i>
              )}
            </div>
          </Box>

          {/* Excursions */}
          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              cursor: 'pointer',
              borderRadius: '8px',
            }}
          >
            <Typography variant="h5" fontSize="18px" fontWeight="bold">
              Excursions
            </Typography>
            <div style={{ display: 'grid', gap: '8px' }}>
              {excursions?.length > 0 ? (
                excursions.map((excursion) => (
                  <Box
                    key={excursion.ExcursionID}
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={1}
                    width="100%"
                  >
                    <FormControlLabel
                      sx={{ display: 'flex', m: 0, flex: 1, minWidth: 200 }}
                      control={
                        <Checkbox
                          checked={details.excursions.includes(excursion.ExcursionID)}
                          onChange={(e) => handleChecked(e, excursion.ExcursionID)}
                          color="success"
                        />
                      }
                      label={excursion.ExcursionName}
                    />
                    {details.excursions.includes(excursion.ExcursionID) && (
                      <Box display="flex" flexDirection="column" gap="2px">
                        <Typography variant="caption" sx={{ visibility: 'hidden' }}>
                          &nbsp;
                        </Typography>
                        <TextField
                          size="small"
                          variant="filled"
                          type="number"
                          label="Max cap (blank = no limit)"
                          disabled={!can('availability.edit')}
                          value={maxQtyByExcursion[excursion.ExcursionID] ?? ''}
                          onChange={(e) => setMaxQtyFor(excursion.ExcursionID, e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ width: 200 }}
                        />
                      </Box>
                    )}
                    {details.excursions.includes(excursion.ExcursionID) && (
                      <Box display="flex" flexDirection="column" gap="2px">
                        {excursion.ExcursionPrice != null && (
                          <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                            Global Price: {excursion.ExcursionPrice}
                          </Typography>
                        )}
                        <TextField
                          size="small"
                          variant="filled"
                          type="number"
                          label="Price override (blank = global)"
                          disabled={!can('prices.edit')}
                          value={priceByExcursion[excursion.ExcursionID] ?? ''}
                          onChange={(e) => setPriceFor(excursion.ExcursionID, e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 220 }}
                        />
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <i>Please choose a Destination.</i>
              )}
            </div>
          </Box>

          {/* PDF Upload */}
          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
              border: `1px dashed ${colors.grey[500]}`,
            }}
          >
            <Typography variant="h5" fontSize="16px" fontWeight="bold" sx={{ color: colors.grey[100] }}>
              Schedule PDF (Optional)
            </Typography>
            <Box display="flex" alignItems="center" gap="15px" flexWrap="wrap">
              <Button
                variant="contained"
                component="label"
                disabled={pdfLoading}
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  '&:hover': { backgroundColor: colors.blueAccent[800] },
                }}
              >
                {details.pdf ? 'Change PDF' : 'Upload PDF'}
                <input type="file" hidden accept="application/pdf" onChange={handlePdfUpload} />
              </Button>

              {details.pdf && (
                <Box display="flex" alignItems="center" gap="10px">
                  <Typography variant="body2" sx={{ color: colors.greenAccent[500], fontWeight: '500', mr: '10px' }}>
                    {details.pdf_raw || 'Schedule Uploaded'}
                  </Typography>
                  <Box display="flex" gap="10px">
                    <a href={details.pdf} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<VisibilityIcon />}
                        sx={{
                          fontWeight: 'bold',
                          color: '#fff',
                          textTransform: 'uppercase',
                          '& .MuiButton-startIcon': { mr: '5px' },
                        }}
                      >
                        View
                      </Button>
                    </a>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: colors.redAccent[600],
                        fontWeight: 'bold',
                        color: '#fff',
                        textTransform: 'uppercase',
                        '&:hover': { backgroundColor: colors.redAccent[700] },
                        '& .MuiButton-startIcon': { mr: '5px' },
                      }}
                      startIcon={<DeleteIcon />}
                      onClick={handleClearPdf}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            {pdfLoading && (
              <Typography variant="caption" sx={{ color: colors.blueAccent[200] }}>
                Uploading schedule...
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            sx={{ ...saveBtnSx(colors), padding: '10px 30px' }}
            disabled={loading || Boolean(guideFeeTierValidationError)}
            title={guideFeeTierValidationError || undefined}
          >
            {loading ? (
              <>
                <CircularProgress color="secondary" size="20px" sx={{ marginRight: '10px' }} />
                Updating
              </>
            ) : (
              'Update'
            )}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CountryExcursionDetail;
